import React, { useEffect, useRef, useState } from "react";
import {
  service1, service2, service3, service4,
  service5, service6, service7, service8,
} from "../../Assets.tsx";

/* Colours: text #4e5356 | buttons #2596be | headings #4573a6
   Fonts  : Ubuntu Bold (headings) / Open Sans Regular (body)
   Idea   : an interactive service explorer. Pick a service on the left (or let it
            auto-play - a progress line fills downward on the active one) and the
            preview on the right unveils from top to bottom. */

const services = [
  { id: 6, title: "Web Development", description: "We understand the importance of a well-designed, functional website. Our expert developers ensure high performance and reliability. Let us help you create an engaging digital experience.", imgSrc: service6, alt: "web-development", tag: "Frontend & Backend" },
  { id: 1, title: "Android Development", description: "We bring a fresh perspective to Android development. We craft robust, scalable, and high-performance mobile applications. Your ideas, our expertise, perfect synergy!", imgSrc: service1, alt: "android", tag: "Kotlin & Java" },
  { id: 2, title: "iOS Development", description: "We're passionate about delivering high-quality iOS apps. Our team is skilled in Swift and React Native for seamless iOS applications. Let's build the next big app together.", imgSrc: service2, alt: "ios", tag: "Swift & React Native" },
  { id: 5, title: "React Native Development", description: "We specialize in crafting high-performance mobile apps. Cross-platform compatibility with a native feel. React Native allows faster development and greater efficiency.", imgSrc: service5, alt: "react-native", tag: "Cross-platform" },
  { id: 3, title: "API Development", description: "We build robust and scalable APIs using NodeJS, PHP, and more. Our APIs ensure seamless integrations for your applications. Secure, scalable, and performance-driven solutions.", imgSrc: service3, alt: "api", tag: "Node & PHP" },
  { id: 4, title: "SEO", description: "We help improve your website's visibility and drive organic traffic. With strategic keyword optimization and content strategies, we help you rank higher on search engines.", imgSrc: service4, alt: "seo", tag: "Organic Growth" },
  { id: 7, title: "Cross-Platform Creation", description: "We build seamless cross-platform experiences. Our focus is on performance and compatibility across all devices. Create once, run anywhere with cross-platform solutions.", imgSrc: service8, alt: "cross-platform", tag: "All Devices" },
];

const STYLES = `
@import url("https://fonts.googleapis.com/css2?family=Open+Sans:wght@400&family=Ubuntu:wght@700&display=swap");
.sv{--text:#4e5356;--head:#4573a6;--btn:#2596be;--btn-dark:#1d7fa3;--tint:#eef6fa;--line:#d3e4ed;--time:5s;
  position:relative;overflow:hidden;padding:20px 0 40px;font-family:"Open Sans",sans-serif;font-weight:400;color:var(--text);
background: #ffffff;
.sv::before {
  content: none;
}
  .sv>*{position:relative}

/* ----- title block (lives in this file) ----- */
.sv-head{max-width:1000px;margin:0 auto;padding:70px 24px 24px;text-align:center;opacity:0;transform:translateY(-60px);
  transition:opacity .9s ease,transform .9s cubic-bezier(.2,.8,.2,1)}
.sv-head.in{opacity:1;transform:none}
.sv-head h1{font-family:"Ubuntu",sans-serif;font-weight:700;font-size:45px;line-height:1.15;margin:0;color:var(--head);cursor:default;transition:color .35s,letter-spacing .35s}
.sv-head h1:hover{color:var(--btn);letter-spacing:.6px}
.sv-head h1::after{content:"";display:block;width:70px;height:4px;margin:14px auto 0;border-radius:4px;background:var(--btn)}
.sv-head h2{font-family:"Ubuntu",sans-serif;font-weight:700;font-size:28px;margin:22px 0 14px;color:var(--head)}
.sv-head p{font-size:17px;line-height:1.85;margin:0;color:var(--text)}

/* ----- explorer ----- */
.sv-main{display:grid;grid-template-columns:5fr 7fr;gap:36px;max-width:1180px;margin:0 auto;padding:30px 24px 50px;align-items:stretch}
.sv-list{display:flex;flex-direction:column;gap:8px}
.sv-tab{position:relative;display:flex;align-items:center;gap:14px;width:100%;text-align:left;padding:14px 20px 14px 24px;border:0;border-radius:16px;cursor:pointer;overflow:hidden;
  font-family:"Open Sans",sans-serif;color:var(--text);
  background:linear-gradient(var(--tint),var(--tint)) no-repeat top/100% 0,transparent;
  transition:background-size .4s ease,color .3s,transform .35s,box-shadow .35s}
.sv-tab:hover{background-size:100% 100%,auto}
.sv-tab.on{background:linear-gradient(180deg,#4573a6,#2596be);color:#fff;transform:translateX(10px);box-shadow:0 18px 30px -18px rgba(37,150,190,.8)}
.sv-tab img{width:44px;height:44px;border-radius:50%;object-fit:cover;flex:none;border:2px solid #fff;background:#fff;box-shadow:0 4px 10px -4px rgba(69,115,166,.5)}
.sv-tab b{display:block;font-family:"Ubuntu",sans-serif;font-weight:700;font-size:17px;line-height:1.25}
.sv-tab small{display:block;font-size:12px;margin-top:2px;opacity:.85}
.sv-fill{position:absolute;left:0;top:0;width:4px;height:0;background:#fff;animation:svFillY var(--time) linear forwards}
.sv-main:hover .sv-fill,.sv-main.off .sv-fill{animation-play-state:paused}

/* ----- preview card ----- */
.sv-pre{position:relative;overflow:hidden;display:grid;grid-template-columns:auto 1fr;gap:38px;align-items:center;min-height:400px;padding:44px;
  background:#fff;border:1px solid var(--line);border-radius:28px;box-shadow:0 30px 60px -36px rgba(69,115,166,.6)}
.sv-pre::before{content:"";position:absolute;right:-90px;top:-90px;width:300px;height:300px;border-radius:50%;
  background:radial-gradient(circle,rgba(37,150,190,.18),transparent 70%)}
.sv-pic{position:relative;width:290px;height:340px;padding:8px;border-radius:145px 145px 26px 26px;background:linear-gradient(180deg,#4573a6,#2596be);animation:svUnveil 1s cubic-bezier(.2,.8,.2,1) both}
.sv-pic img{display:block;width:100%;height:100%;object-fit:cover;border-radius:138px 138px 20px 20px;background:var(--tint)}
.sv-info{position:relative;display:flex;flex-direction:column;align-items:flex-start;gap:12px}
.sv-info>*{animation:svDrop .6s cubic-bezier(.2,.8,.2,1) backwards}
.sv-info>*:nth-child(1){animation-delay:.25s}.sv-info>*:nth-child(2){animation-delay:.35s}
.sv-info>*:nth-child(3){animation-delay:.45s}.sv-info>*:nth-child(4){animation-delay:.55s}.sv-info>*:nth-child(5){animation-delay:.65s}
.sv-tag{display:inline-block;background:var(--tint);color:var(--head);font-size:11px;letter-spacing:.6px;text-transform:uppercase;border-radius:999px;padding:5px 14px;border:1px solid var(--line)}
.sv-bar{width:46px;height:3px;border-radius:999px;background:var(--btn)}
.sv-info h3{font-family:"Ubuntu",sans-serif;font-weight:700;font-size:30px;line-height:1.2;margin:0;color:var(--head);transition:color .3s}
.sv-info h3:hover{color:var(--btn)}
.sv-info p{font-size:15.5px;line-height:1.85;margin:0;color:var(--text)}
.sv-btn{display:inline-flex;align-items:center;gap:8px;margin-top:6px;padding:13px 28px;border:0;border-radius:999px;cursor:pointer;font-family:"Open Sans",sans-serif;font-size:14px;color:#fff;
  background:linear-gradient(var(--btn-dark),var(--btn-dark)) no-repeat top/100% 0,var(--btn);transition:background-size .4s ease,box-shadow .3s,transform .3s}
.sv-btn:hover{background-size:100% 100%,auto;box-shadow:0 14px 26px -10px rgba(37,150,190,.7);transform:translateY(2px)}
.sv-btn span{display:inline-block;transition:transform .3s}.sv-btn:hover span{transform:translateY(3px) rotate(90deg)}

/* entrance */
.sv-main:not(.in) .sv-tab,.sv-main:not(.in) .sv-pre{opacity:0}
.sv-main.in .sv-tab{animation:svDrop .7s cubic-bezier(.2,.8,.2,1) backwards}
${Array.from({ length: 8 }, (_, i) => `.sv-main.in .sv-tab:nth-child(${i + 1}){animation-delay:${i * 90}ms}`).join("")}
.sv-main.in .sv-pre{animation:svDrop .9s cubic-bezier(.2,.8,.2,1) .2s backwards}

@keyframes svDrop{from{opacity:0;transform:translateY(-28px)}}
@keyframes svUnveil{from{clip-path:inset(0 0 100% 0)}}
@keyframes svFillY{from{height:0}to{height:100%}}
@keyframes svFillX{from{width:0}to{width:100%}}

@media(max-width:900px){
  .sv-head h1{font-size:34px}.sv-head h2{font-size:24px}
  .sv-main{grid-template-columns:1fr;gap:22px;padding:20px 16px 40px}
  .sv-list{flex-direction:row;overflow-x:auto;padding-bottom:8px;scroll-snap-type:x proximity}
  .sv-tab{flex:0 0 auto;width:auto;min-width:220px;scroll-snap-align:start;background:#fff;border:1px solid var(--line)}
  .sv-tab.on{transform:none;border-color:transparent}
  .sv-fill{top:auto;bottom:0;height:3px;width:0;animation-name:svFillX}
  .sv-pre{grid-template-columns:1fr;gap:26px;padding:28px 22px;min-height:0;justify-items:center}
  .sv-pic{width:230px;height:270px;border-radius:115px 115px 22px 22px}.sv-pic img{border-radius:108px 108px 16px 16px}
  .sv-info{align-items:flex-start;width:100%}.sv-info h3{font-size:25px}
}
@media(prefers-reduced-motion:reduce){
  .sv *,.sv *::before,.sv *::after{animation:none!important;transition:none!important}
  .sv .sv-head,.sv .sv-tab,.sv .sv-pre{opacity:1;transform:none}
}
`;

const useInView = (threshold = 0.15) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, inView };
};

const OurServicesSection = () => {
  const head = useInView();
  const main = useInView(0.2);
  const [active, setActive] = useState(0);
  const s = services[active];

  // keep the active tab visible when the list scrolls sideways (mobile)
  useEffect(() => {
    const list = document.getElementById("sv-list");
    const tab = document.getElementById("sv-tab-" + active);
    if (list && tab && list.scrollWidth > list.clientWidth) {
      list.scrollTo({ left: tab.offsetLeft - list.clientWidth / 2 + tab.clientWidth / 2, behavior: "smooth" });
    }
  }, [active]);

  const next = () => setActive((a) => (a + 1) % services.length);

  return (
    <div className="sv">
      <style>{STYLES}</style>

      <div ref={head.ref} className={"sv-head" + (head.inView ? " in" : "")}>
        <h1>Our Services</h1>
        <h2>What We Offer?</h2>
        <p>
          We provide a comprehensive range of services designed to deliver
          optimal solutions in strategy, consulting, digital technologies, and
          operations. As a forward thinking company specializing in digital
          innovation, we have established strategic partnerships with
          organizations committed to driving technological advancement. With
          extensive experience across industries and business functions, we bring
          valuable expertise to navigate the complexities of today’s rapidly
          evolving digital landscape, ensuring our clients achieve the best
          possible outcomes.
        </p>
      </div>

      <div ref={main.ref} className={"sv-main" + (main.inView ? " in" : " off")}>
        <div className="sv-list" id="sv-list" role="tablist" aria-label="Services">
          {services.map((item, i) => (
            <button
              type="button"
              role="tab"
              aria-selected={i === active}
              id={"sv-tab-" + i}
              key={item.id}
              className={"sv-tab" + (i === active ? " on" : "")}
              onMouseEnter={() => setActive(i)}
              onClick={() => setActive(i)}
            >
              <img src={item.imgSrc} alt="" />
              <span>
                <b>{item.title}</b>
                <small>{item.tag}</small>
              </span>
              {i === active && <i className="sv-fill" onAnimationEnd={next} />}
            </button>
          ))}
        </div>

        <div className="sv-pre" key={s.id}>
          <div className="sv-pic">
            <img src={s.imgSrc} alt={s.alt} />
          </div>
          <div className="sv-info">
            <span className="sv-tag">{s.tag}</span>
            <div className="sv-bar" />
            <h3>{s.title}</h3>
            <p>{s.description}</p>
            <button type="button" className="sv-btn">
              Learn More <span>→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OurServicesSection;